import React, { useContext, useState } from 'react';
import { Field, FieldMetaState, useFormState } from 'react-final-form';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { TickIcon } from '@motorway/mw-highway-code';
import { InputNumericFormat } from '@motorway/mw-highway-code/alpha';
import { SIZE } from '@motorway/mw-highway-code/enums';
import { Intent } from '@motorway/mw-highway-code/src/types';
import { TippyProps } from '@tippy.js/react';

import cypressIds from 'CypressId';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { VALUATIONS_GA_TAGS } from 'Utilities/analytics';
import { stringNumberToInt } from 'Utilities/formatter';
import {
  checkMotMileage,
  composeValidators,
  getFieldState,
  INTENT,
  numberCommaSeparatedValidator,
} from 'Utilities/formValidators';
import { applyCypressData } from 'Utilities/index';
import { getMileage } from 'Utilities/mileage';

import { InputLabel } from 'Components/inputLabel/InputLabel';
import { FeedbackTooltip } from 'Storybook/components/local/FeedbackTooltip/FeedbackTooltip';

import { useBreakpoint } from '../../../../components/misc/Breakpoints/Breakpoints';

import LocalTexts from './MileageInputTexts.json';

import styles from './MileageInput.scss';

const LocalT = new MDText(LocalTexts);

const t = (key: string) => `${LocalT.translate(key)}`;

const texts = {
  mileageChecker: {
    expired: t('mileageChecker.expiredLabel'),
    label: t('mileageChecker.label'),
    populated: t('mileageChecker.populatedLabel'),
  },
  mileageField: {
    placeholder: t('mileageField.placeholder'),
  },
};

const getInputLabel = (isPadded: boolean, mileageValue: number | string) => {
  const { mileageChecker: { expired, label, populated } } = texts;
  if (isPadded) {
    return expired;
  }

  const numericValue = typeof mileageValue === 'number' ? mileageValue : parseFloat(mileageValue);

  if (numericValue > 0) {
    return populated;
  }
  return label;
};

const mileageInputAttributes = {
  ...applyCypressData(cypressIds.fields.mileageInput),
  autoComplete: 'off',
  autoCorrect: 'off',
  inputMode: 'decimal', // Ensure we get numeric keyboard on mobile that supports commas for mileage input
  maxLength: 7,
  pattern: '[0-9]+(,[0-9]+)*',
};

export interface MileageInputProps {
  analyticsEvents?: {
    onInputFocus?: () => void;
  };
  className?: string;
  getFeedback?: (meta: FieldMetaState<string | number>) => string | undefined;
  getIntent?: (meta: FieldMetaState<string | number>, validateOnInit?: boolean) => Intent | null;
  isCarValueTracker?: boolean;
  isHero?: boolean;
  isHighMileageModal?: boolean;
  label?: React.ReactNode;
  mileage?: number | string;
  noLabel?: boolean;
  padded?: boolean;
  tooltipPosition?: TippyProps['placement'];
  validateOnInit?: boolean;
}

const getDefaultErrorMessage = (meta: FieldMetaState<string | number>) => meta.error;

export const MileageInput = ({
  analyticsEvents = undefined,
  className = undefined,
  getFeedback = getDefaultErrorMessage,
  getIntent = getFieldState,
  isCarValueTracker = false,
  isHero = false,
  isHighMileageModal = false,
  label,
  mileage: initialMileage,
  noLabel = false,
  padded = false,
  tooltipPosition = 'top',
  validateOnInit = false,
}: MileageInputProps) => {
  const { maxWidth } = useBreakpoint();

  const { vehicleLookupState: lookupVehicle } = useContext(VehicleLookupContext);

  const { pristine, values } = useFormState();
  const [mileageValue] = useState(
    stringNumberToInt(initialMileage) || (getMileage({ vehicle: lookupVehicle }) || ''),
  );
  if (pristine) {
    // eslint-disable-next-line react-compiler/react-compiler
    values.mileage = mileageValue;
  }

  const isMobile = maxWidth.breakpointMobile;
  const inputLabel = getInputLabel(padded, mileageValue);

  const onInputErrorState = (value: number | string) => {
    if (isCarValueTracker) {
      return;
    }

    const analyticsEvent = isHighMileageModal
      ? VALUATIONS_GA_TAGS.MILEAGE_MODAL_ERROR_TOOLTIP
      : VALUATIONS_GA_TAGS.MILEAGE_ERROR_TOOLTIP;

    analyticsEvent({ mileage: value });
  };

  const showEstimateTooltip = !!lookupVehicle?.mileageEst;

  return (
    <div className={cx(styles.component, className, { [styles.padded]: padded })}>
      <Field
        id="mileage-input"
        name="mileage"
        // https://codesandbox.io/s/10rzowm323?file=/index.js
        parse={(val) => stringNumberToInt(val) || ''}
        validate={composeValidators(
          numberCommaSeparatedValidator(LocalT.translate('mileageChecker.error.errorMessage')),
          checkMotMileage(t('mileageChecker.error.tooLow'), lookupVehicle?.mileageLastMot),
        )}
      >
        {({ id, input, meta }) => {
          const intent = getIntent(meta, validateOnInit && input.value);
          const icon = intent === INTENT.SUCCESS ? (iconProps: object) => <TickIcon data-tick {...iconProps} size="small" /> : undefined;

          return (
            <FeedbackTooltip
              appendTo={'parent'}
              className={cx(styles.tooltip, {
                [styles.hidden]: !meta.error,
              })}
              content={getFeedback(meta)}
              distance={noLabel ? 16 : -16}
              intent={intent}
              maxWidth={isMobile ? 300 : 250}
              placement={tooltipPosition}
            >
              <InputNumericFormat
                fullWidth
                append={icon}
                data-intent={intent}
                formProps={{
                  input: {
                    ...input,
                    ...mileageInputAttributes,
                    onFocus: (e) => {
                      input.onFocus(e);
                      if (isHero) {
                        VALUATIONS_GA_TAGS.HERO_MILEAGE_INPUT_FOCUSED();
                      }
                      analyticsEvents?.onInputFocus?.();
                    },
                    placeholder: texts.mileageField.placeholder,
                  },
                }}
                id={id}
                intent={(() => {
                  if (intent === INTENT.ERROR) {
                    onInputErrorState(input.value);
                  }
                  return intent;
                })() as Intent}
                label={label ?? <InputLabel
                  anchorProps={{
                    background: true,
                    onMouseEnter: () => {
                      VALUATIONS_GA_TAGS.HERO_ESTIMATED_MILEAGE_TOOLTIP_SHOWN();
                    },
                  }}
                  labelText={inputLabel}
                  tooltip={showEstimateTooltip
                    ? LocalT.translate('mileageChecker.tooltip.detail')
                    : undefined}
                  tooltipSibling={padded && showEstimateTooltip ? <span className={cx('mw-link mw-link-blue', styles.tooltipLabel)}>{LocalT.translate('mileageChecker.tooltip.label')}</span> : null} />}
                prepend={icon}
                showLabel={!noLabel}
                size={padded ? SIZE.MEDIUM : SIZE.LARGE} />
            </FeedbackTooltip>
          );
        }}
      </Field>
    </div>
  );
};
